/**
 * segmentSettingsAccessor.js — the ONE casing-agnostic interface to a segment's
 * stored `settings` (fill / palette / color / geometry) fields.
 *
 * WHY THIS EXISTS (W1-1, the modernization flagship — Gordon 2026-07-21):
 * The same snake_case-vs-camelCase defect broke TWO shipped features:
 *   · #2117 — the Gallery/Explore pill palette-primary color was INERT on real
 *     cards because a segment's `settings` arrives from the API in SNAKE_CASE
 *     (`palette_data`, `segment_color_manual`) while the resolver read camelCase.
 *   · #2120 — segment-fill GIFs stayed frozen on hover because the legacy fill
 *     fields (`bg_wallpaper`, `current_bg`, `segment_bg_color`) also arrive snake
 *     while the resolver read camelCase.
 * Each was patched LOCALLY (a `camel ?? snake` read in fillModelFeed; a
 * whole-object `convertSnakeToCamel` in segmentPaletteFromFill). That is a bug
 * CLASS: ANY consumer that reads a raw segment-settings key in one casing can hit
 * it again the moment it meets data in the other casing.
 *
 * This module KILLS the class by construction: every consumer normalises through
 * ONE interface here, so no render/fill/palette/color reader ever spells a raw
 * snake/camel key pair itself again. Built ON #2117's src/utils/caseConvert.js —
 * it does NOT re-implement the conversion (single source of truth), and it stays
 * out of the react-froala barrel so any Jest consumer can import it (see
 * caseConvert.js's header for that story).
 *
 * TWO primitives, because the render path has TWO honest casing situations:
 *   1. pickSetting(obj, camelKey) — read ONE logical field, camelCase spelling
 *      winning over snake_case, WITHOUT rewriting the object. This exactly
 *      reproduces the pre-existing `s.foo ?? s.foo_bar` precedence (incl. the
 *      "in-memory camel edit beats a stale snake reload" tie-break), and it never
 *      touches the sibling snake-only `fill_*` model-field dual-write contract
 *      that lives in the same object. Used by the fill resolver's legacy reads.
 *   2. normalizeSegmentSettings(settings) — return a canonical camelCase view of a
 *      whole settings object (shallow). Null/non-object safe, never throws,
 *      idempotent on already-camel input, nested blobs (`fill`/`paletteData`/
 *      `gradient`) left intact. Used by the palette/color consumers, which read a
 *      broad, evolving field set and already normalised the whole object.
 *
 * Pure: no React, no redux, no DOM, no I/O.
 */

import { convertSnakeToCamel } from "./caseConvert";

/** camelCase -> snake_case, inverse of caseConvert's per-key transform. */
const toSnakeKey = (camelKey) =>
  String(camelKey).replace(/[A-Z]/g, (m) => "_" + m.toLowerCase());

/**
 * Read ONE logical field from a settings-like object regardless of casing.
 * camelCase spelling WINS over snake_case when both are present — matching the
 * render path's pre-existing `obj.currentBg ?? obj.current_bg` precedence, so an
 * in-memory (camelCase) edit is never overridden by a stale (snake_case) reload.
 * Null/non-object input returns undefined.
 *
 * @param {object|null|undefined} obj a settings-like object in either casing
 * @param {string} camelKey the canonical camelCase field name (e.g. "currentBg")
 * @returns {*} the field value, or undefined
 */
export const pickSetting = (obj, camelKey) => {
  if (!obj || typeof obj !== "object") return undefined;
  const camel = obj[camelKey];
  if (camel !== undefined) return camel;
  return obj[toSnakeKey(camelKey)];
};

/**
 * Normalise a whole settings object to canonical camelCase keys (shallow).
 *
 * Contract vs. raw convertSnakeToCamel:
 *   · null / undefined / non-object  -> {}         (convertSnakeToCamel THROWS)
 *   · already-camelCase              -> equivalent (idempotent; camel keys carry
 *                                       no `_[a-z]`, so they pass through)
 *   · nested blobs (fill/paletteData/gradient) are untouched (shallow — only
 *     top-level keys are converted; the V2 model blob is already camelCase)
 *
 * Use this for consumers that read a broad settings field set (palette/color).
 * For a single legacy fill field where in-memory-camel must beat stale-snake,
 * prefer pickSetting (it never rewrites the sibling snake-only fill_* fields).
 *
 * @param {object|null|undefined} settings
 * @returns {object} a camelCase-keyed settings object (a NEW object; never null)
 */
export const normalizeSegmentSettings = (settings) => {
  if (!settings || typeof settings !== "object") return {};
  return convertSnakeToCamel(settings);
};
